/**
 * Копирование секрета в буфер обмена (§7.1, §15.3: PIN, ссылка приглашения,
 * пароль сотрудника показываются один раз).
 *
 * Во встроенном режиме страница живёт в iframe портала, и разрешения
 * `clipboard-write` у неё нет: `navigator.clipboard` отклоняет запись. Запасной
 * путь — `document.execCommand('copy')`, но верить его ответу нельзя: в том же
 * iframe он возвращает `true`, ничего не скопировав, и экран рапортует
 * «Скопировано», а буфер пуст (наблюдалось в портале 19.08.2026). Для секрета,
 * который показывается один раз, это худший из возможных исходов.
 *
 * Поэтому успех подтверждается событием `copy`: браузер шлёт его только когда
 * действительно выполняет копирование, и в обработчике значение кладётся в
 * буфер напрямую. Не пришло событие — значит копирования не было, и функция
 * честно возвращает `false`; вызывающий экран обязан предложить выделить
 * значение руками.
 */
export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Отказ разрешения или небезопасный контекст — пробуем запасной путь
  }
  return legacyCopy(value);
}

function legacyCopy(value: string): boolean {
  let copied = false;
  const onCopy = (event: ClipboardEvent) => {
    event.clipboardData?.setData('text/plain', value);
    event.preventDefault();
    copied = true;
  };

  const active = document.activeElement as HTMLElement | null;
  const field = document.createElement('textarea');
  field.value = value;
  field.setAttribute('readonly', '');
  // Поле не должно мигать на экране и уводить прокрутку страницы
  field.style.position = 'fixed';
  field.style.top = '0';
  field.style.opacity = '0';
  document.body.appendChild(field);
  document.addEventListener('copy', onCopy);

  try {
    // Без фокуса выделение в iframe не всегда доходит до команды копирования
    field.focus();
    field.select();
    field.setSelectionRange(0, value.length);
    document.execCommand('copy');
  } catch {
    // Команда недоступна — событие не придёт, и это уже учтено ответом
  } finally {
    document.removeEventListener('copy', onCopy);
    field.remove();
    active?.focus?.();
  }

  return copied;
}
